import { Icon, PrimaryButton } from "@fluentui/react";
import { getTheme } from "@fluentui/react/lib/Styling";
import { EbookIcon } from "../pages/Landing/assets/eBooks";
import { NoRecordTypes } from "./management/types";

interface NoRecordsProps {
  entryType: NoRecordTypes;
  handleCreateOpen?: () => void;
}

const header = (entryType: NoRecordTypes): string => {
  switch (entryType) {
    case "collection":
      return "You have no collections yet";
    case "item":
      return "No STAC items to display yet";
    case "ingestion":
      return "Create ingestion";
    case "credential":
      return "You have no ingestion sources yet";
    default:
      return "No Results";
  }
};

const body = (entryType: NoRecordTypes): string => {
  switch (entryType) {
    case "collection":
      return "Create a collection to begin ingesting data into your collection.";
    case "item":
      return "Create an ingestion then complete a run to make STAC items available for your collection.";
    case "ingestion":
      return "Create an ingestion then start a run to bring data into your collection.";
    case "credential":
      return "Create an ingestion source first, and then create an ingestion to bring data into your collection.";
    default:
      return "";
  }
};

const buttonLabel = (entryType: NoRecordTypes): string => {
  switch (entryType) {
    case "collection":
      return "Create collection";
    case "item":
      return "Create ingestion";
    case "ingestion":
      return "Create ingestion";
    case "credential":
      return "Create ingestion source";
    default:
      return "";
  }
};

const learnMoreLink = (entryType: NoRecordTypes): string => {
  switch (entryType) {
    case "collection":
      return "http://aka.ms/geoCatalogDocs";
    case "item":
      return "http://aka.ms/geoCatalogDocs";
    case "ingestion":
      return "http://aka.ms/mpcprobulkingest";
    case "credential":
      return "http://aka.ms/mpcprobulkingest";
    default:
      return "http://aka.ms/geoCatalogDocs";
  }
};

export const NoRecords: React.FC<NoRecordsProps> = ({
  entryType,
  handleCreateOpen,
}) => {
  return (
    <div data-cy="no-filtered-collection-results" style={bodyStyle}>
      <div className="empty-state-icon">
        <EbookIcon />
      </div>
      <div className="text">
        <div className="header-desc">
          <div style={noCollectionsStyle.title}> {header(entryType)} </div>
          <div style={noCollectionsStyle.subtitleOptional}> {body(entryType)} </div>
        </div>
        <PrimaryButton
          style={noCollectionsStyle.button}
          text={buttonLabel(entryType)}
          onClick={handleCreateOpen}
        />
        <div style={noCollectionsStyle.frame631089}>
          <div style={noCollectionsStyle.hyperlink}>
            <a href={learnMoreLink(entryType)} target="_blank" rel="noreferrer">
              Learn more
            </a>
            <Icon iconName="NavigateExternalInline" className="icon" />
          </div>
        </div>
      </div>
    </div>
  );
};

const theme = getTheme();
const bodyStyle = {
  alignItems: "center",
  textAlign: "center",
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: 3,
  padding: "0 15px",
  margin: 20,
  marginLeft: 0,
} as React.CSSProperties;

export const noCollectionsStyle = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  title: {
    textAlign: "center",
    fontFamily:
      'var(--az-text-greeting-font-family, "SegoeUi-Semibold", sans-serif)',
    fontSize: "var(--az-text-greeting-font-size, 20px)",
    lineHeight: "var(--az-text-greeting-line-height, 28px)",
    fontWeight: "var(--az-text-greeting-font-weight, 600)",
    position: "relative",
    marginBottom: "10px",
  } as React.CSSProperties,
  subtitleOptional: {
    textAlign: "center",
    fontFamily: 'var(--az-text-body-font-family, "SegoeUi-Regular", sans-serif)',
    fontSize: "var(--az-text-body-font-size, 13px)",
    lineHeight: "var(--az-text-body-line-height, 18px)",
    fontWeight: "var(--az-text-body-font-weight, 400)",
    position: "relative",
    marginBottom: "10px",
  } as React.CSSProperties,
  button: {
    marginBottom: "10px",
  } as React.CSSProperties,
  frame631089: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "center",
    justifyContent: "flex-start",
    flexShrink: 0,
    position: "relative",
  } as React.CSSProperties,
  hyperlink: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    alignItems: "center",
    justifyContent: "flex-start",
    flexShrink: 0,
    position: "relative",
    overflow: "hidden",
  } as React.CSSProperties,
};
